import Footer from "./common/Footer";
import HeaderDashboard from "./common/HeaderDashboard";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Select from "react-select";
import "@google/model-viewer";

import level2Data from "../data/level2.json";
import level3Data from "../data/level3.json";
import level4Data from "../data/level4.json";
import axios from "axios";

function UploadModel() {
    const { user } = useAuth();
    const navigate = useNavigate();

    /* =======================
       STATE
    ======================= */
    const [selectedLevel2, setSelectedLevel2] = useState(null);
    const [selectedLevel3, setSelectedLevel3] = useState(null);
    const [selectedLevel4, setSelectedLevel4] = useState(null);

    const [glbFile, setGlbFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [materialsUI, setMaterialsUI] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [disabled, setDisabled] = useState(false);

    const modelRef = useRef(null);

    const [dragIndex, setDragIndex] = useState(null);

    const handleDragStart = (index) => {
        setDragIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // REQUIRED
    };

    const handleDrop = (dropIndex) => {
        if (dragIndex === null || dragIndex === dropIndex) return;

        const updated = [...materialsUI];
        const [movedItem] = updated.splice(dragIndex, 1);
        updated.splice(dropIndex, 0, movedItem);

        setMaterialsUI(updated);
        setDragIndex(null);
    };

    /* =======================
       OPTIONS
    ======================= */
    const level2Options = [
        { value: "", label: "Select" },
        ...Object.entries(level2Data).map(([key, value]) => ({
            value: key,
            label: value.name,
            slug: value.slug,
        })),
    ];

    const level3Options = selectedLevel2
        ? [
            { value: "", label: "Select" },
            ...Object.entries(level3Data)
                .filter(([key]) =>
                    key.split("/").includes(selectedLevel2.slug)
                )
                .map(([key, value]) => ({
                    value: key,
                    label: value.name,
                    slug: value.slug,
                })),
        ]
        : [];

    const level4Options = selectedLevel3
        ? [
            { value: "", label: "Select" },
            ...Object.entries(level4Data)
                .filter(([key]) =>
                    key.split("/").includes(selectedLevel3.slug)
                )
                .map(([key, value]) => ({
                    value: key,
                    label: value.name,
                    slug: value.slug,
                })),
        ]
        : [];

    /* =======================
       FILE HANDLER
    ======================= */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setGlbFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setMaterialsUI([]);
    };

    async function getModelDetail(level2, level3, level4) {
        /* 4️⃣ Prepare payload for DB */
        const payload = {
            level1: "torso",
            level2: selectedLevel2.slug,
            level3: selectedLevel3.slug,
            level4: selectedLevel4?.slug || "",
        };
        /* 5️⃣ Get model Detail */
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/get-glb-model-detail`,
            payload
        );

        return response.data; // 🔑 IMPORTANT

    }

    /* =======================
       MODEL LOAD
    ======================= */
    const onLoad = async () => {
        const modelViewer = modelRef.current;
        if (!modelViewer?.model) return;

        // 1️⃣ Normalize materials from GLB
        const rawMaterials = modelViewer.model.materials;
        const glbMaterials = Array.isArray(rawMaterials)
            ? rawMaterials
            : Array.from(rawMaterials.values());

        if (!glbMaterials.length) {
            console.warn("No materials found in GLB");
            return;
        }

        // 2️⃣ Load DB materials
        let dbMaterials = [];
        try {
            const dbResponse = await getModelDetail(
                selectedLevel2.slug,
                selectedLevel3.slug,
                selectedLevel4?.slug
            );

            dbMaterials = dbResponse?.materials || [];
        } catch (err) {
            console.error("Failed to fetch model detail:", err);
        }

        // 3️⃣ Map DB materials by originalName for fast lookup
        const dbMaterialMap = new Map(
            dbMaterials.map(m => [m.originalName, m])
        );

        // 4️⃣ Build UI material list from GLB (source of truth)
        const uiData = glbMaterials.map((mat, index) => {
            const originalName = mat.name || `Material ${index}`;

            const dbMat = dbMaterialMap.get(originalName);

            return {
                index,
                originalName: dbMat?.originalName || originalName,   // show DB name if exists
                displayName: dbMat?.displayName || originalName,
                order: dbMat?.order ?? index + 1,
                materialRef: mat,
                visible: true
            };
        });

        // 5️⃣ Detect removed DB materials (optional: for cleanup UI or API call)
        const glbNameSet = new Set(
            glbMaterials.map((m, i) => m.name || `Material ${i}`)
        );

        const removedFromGlb = dbMaterials.filter(
            m => !glbNameSet.has(m.originalName)
        );

        if (removedFromGlb.length) {
            console.warn("Materials removed from GLB:", removedFromGlb);
            // Optional: call API to remove them from DB
            // await removeMaterialsFromDB(removedFromGlb);
        }

        // 6️⃣ Sort by DB order (if exists)
        uiData.sort((a, b) => a.order - b.order);

        setMaterialsUI(uiData);
    };
    /* =======================
       MATERIAL CONTROLS
    ======================= */

    const toggleMaterial = (i) => {
        setMaterialsUI((prev) =>
            prev.map((item, idx) => {
                if (idx !== i) return item;

                const mat = item.materialRef;
                if (!mat || !mat.pbrMetallicRoughness) return item;

                const visible = !item.visible;

                // 🔒 Preserve original base color ONCE
                if (!mat._originalBaseColor) {
                    if (mat.pbrMetallicRoughness.baseColorFactor) {
                        mat._originalBaseColor = [
                            ...mat.pbrMetallicRoughness.baseColorFactor,
                        ];
                    } else {
                        mat.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
                        mat._originalBaseColor = [1, 1, 1, 1];
                    }
                }

                // 🔁 Match loadMenu behavior
                mat.setAlphaMode("MASK");
                mat.setAlphaCutoff(0.5);

                const [r, g, b, a] = mat._originalBaseColor;

                mat.pbrMetallicRoughness.setBaseColorFactor([
                    r,
                    g,
                    b,
                    visible ? a : 0,
                ]);

                return { ...item, visible };
            })
        );
    };

    const updateDisplayName = (i, value) => {
        setMaterialsUI((prev) =>
            prev.map((item, idx) =>
                idx === i ? { ...item, displayName: value } : item
            )
        );
    };

    /* =======================
       SUBMIT (ready for API)
    ======================= */
    const handleSubmit = async () => {
        try {
            setDisabled(true);

            /* 1️⃣ Build materials array (optional) */
            const materialConfig = materialsUI.map((m, order) => ({
                originalName: m.originalName,
                displayName: m.displayName,
                order,
            }));


            /* 4️⃣ Prepare payload for DB */
            const payload = {
                level1: "torso",
                level2: selectedLevel2.slug,
                level3: selectedLevel3.slug,
                level4: selectedLevel4?.slug || "",
                file_name: glbFile.name,
                materials: materialConfig.length ? materialConfig : undefined,
            };
            /* 5️⃣ Save model metadata */
            await axios.post(
                `${process.env.REACT_APP_API_URL}/add-glb-model`,
                payload
            );

            /* 6️⃣ Success */
            alert("GLB model uploaded successfully!");
            window.location.reload();

        } catch (error) {
            console.error("❌ Upload failed:", error);

            if (error?.response) {
                setErrorMessage(error.response.data?.message || "Server error");
            } else {
                setErrorMessage("Upload failed. Check S3, CORS, or network.");
            }

            setDisabled(false);
        }
    };



    useEffect(() => {
        const modelViewer = modelRef.current;
        if (!modelViewer) return;

        const handleLoad = () => onLoad();

        modelViewer.addEventListener("load", handleLoad);

        // Clean up
        return () => {
            modelViewer.removeEventListener("load", handleLoad);
        };
    }, [previewUrl]); // ✅ attach listener whenever previewUrl changes
    return (
        <div className="w-100 h-100 bg-green5">
            <HeaderDashboard />

            <main className="main-section mx-4 rounded-top-3 bg-white custom-scroller overflow-auto">
                <div className="container pt-4 pb-5">

                    <h1 className="fw-bold">Upload Model</h1>

                    {/* FORM */}
                    <form className="row g-3 mt-3">

                        <div className="col-md-6">
                            <label>Level 2 *</label>
                            <Select
                                options={level2Options}
                                value={selectedLevel2}
                                onChange={(o) => {
                                    if (!o || o.value === "") {
                                        setSelectedLevel2(null);
                                    } else {
                                        setSelectedLevel2(o);
                                    }

                                    setSelectedLevel3(null);
                                    setSelectedLevel4(null);
                                }}
                            />
                        </div>


                        <div className="col-md-6">
                            <label>Level 4</label>
                            <Select
                                options={level4Options}
                                value={selectedLevel4}
                                onChange={setSelectedLevel4}
                                isDisabled={!selectedLevel3}
                            />
                        </div>

                        <div className="col-md-6">
                            <label>Level 3</label>
                            <Select
                                options={level3Options}
                                value={selectedLevel3}
                                onChange={(o) => {
                                    setSelectedLevel3(o);
                                    setSelectedLevel4(null);
                                }}
                                isDisabled={!selectedLevel2}
                            />
                        </div>


                        <div className="col-md-6">
                            <label>Browse GLB *</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".glb"
                                onChange={handleFileChange}
                            />
                        </div>

                    </form>

                    {/* PREVIEW + MATERIAL PANEL */}
                    {previewUrl && (
                        <div className="mt-4 d-flex gap-4 col-md-12">

                            {/* MODEL */}
                            <model-viewer
                                ref={modelRef}
                                src={previewUrl}
                                camera-controls
                                environment-image="/images/hdri/neutral.hdr"
                                interaction-prompt="none"
                                style={{ width: "600px", height: "500px", background: "#f5f5f5" }}
                                onLoad={onLoad}  // ✅ React way
                                className="col-md-6"
                            />

                            {/* MATERIAL PANEL */}
                            <div className="col-md-6">
                                <h5 className="fw-bold mb-2">Materials</h5>

                                <div className="border rounded p-2" style={{ maxHeight: "450px", overflowY: "auto" }}>
                                    {materialsUI.map((item, i) => (
                                        <div
                                            key={item.originalName + i}
                                            draggable
                                            onDragStart={() => handleDragStart(i)}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(i)}
                                            className="d-flex align-items-center gap-4 mb-2 p-2 border rounded bg-white"
                                            style={{ cursor: "grab" }}
                                        >
                                            {/* Hide / Show */}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleMaterial(i)}
                                                title={item.visible ? "Hide" : "Show"}
                                            >
                                                {item.visible ? "👁" : "🚫"}
                                            </button>
                                            {/* Drag handle */}
                                            <span
                                                className="text-muted"
                                                style={{ cursor: "grab", fontSize: "18px" }}
                                                title="Drag to reorder"
                                            >
                                                ☰
                                            </span>

                                            {/* Order number */}
                                            <span className="badge bg-secondary">{i + 1}</span>

                                            {/* Original name */}
                                            <span
                                                className="text-muted small"
                                                style={{ minWidth: "200px" }}
                                            >
                                                {item.originalName}
                                            </span>

                                            {/* Editable display name */}
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={item.displayName}
                                                onChange={(e) =>
                                                    updateDisplayName(i, e.target.value)
                                                }
                                                placeholder="Portal name"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary w-100 mt-3"
                                    onClick={handleSubmit}
                                >
                                    Save Model Config
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <Footer />
        </div>
    );
}

export default UploadModel;
